"use client";

import { Button } from "@/components/atoms/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/molecules/Table";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

// Types based on API data structure
interface FeeTab {
  id: number;
  program_fee_id: number;
  name: string;
  text_color?: string | null;
  bg_color?: string | null;
  active_text_color?: string | null;
  active_bg_color?: string | null;
  category_header: string;
  category_header_text_color?: string | null;
  category_header_bg_color?: string | null;
  category_header_border_color?: string | null;
  order_index: number;
  is_active: number;
  status: number;
}

interface FeeRow {
  id: number;
  program_fee_id: number;
  fee_type_id: number;
  name: string;
  text_color?: string | null;
  bg_color?: string | null;
  border_color?: string | null;
  order_index: number;
  is_active: number;
  status: number;
}

interface FeeColumn {
  id: number;
  program_fee_id: number;
  fee_type_id: number;
  name: string;
  semester_number?: number | null;
  text_color?: string | null;
  bg_color?: string | null;
  border_color?: string | null;
  order_index: number;
  is_active: number;
  status: number;
}

interface FeeValue {
  id: number;
  fee_type_id: number;
  category_id: number;
  semester_id: number;
  value: string;
  text_color?: string | null;
  bg_color?: string | null;
  border_color?: string | null;
  is_active: number;
  status: number;
}

interface ProgramFeeData {
  id: number;
  page_id: number;
  component_id: number;
  title: string;
  title_text_color?: string;
  note?: string;
  note_text_color?: string;
  other_fee?: string;
  background_color?: string;
  table_header_background_color?: string;
  // CTA button fields
  cta_button_text?: string;
  cta_button_text_color?: string;
  cta_button_color?: string;
  cta_button_icon?: string | null;
  cta_button_icon_alt?: string;
  status: number;
  tabs: FeeTab[];
  rows: FeeRow[];
  columns: FeeColumn[];
  values: FeeValue[];
}

interface ProgramFee1Props {
  data?: {
    data?: ProgramFeeData;
  };
  className?: string;
}

function ProgramFee1({ data, className }: ProgramFee1Props) {
  const isMobile = useIsMobile(640);
  const ref = useRef<HTMLElement>(null);
  const previousTabIdRef = useRef<number>(0);
  const dispatch = useDispatch();

  const feeData = data?.data;

  // Get active tabs sorted by order
  const activeTabs = useMemo(() => {
    if (!feeData?.tabs) return [];
    return feeData.tabs
      .filter((tab) => tab.is_active && tab.status)
      .sort((a, b) => a.order_index - b.order_index);
  }, [feeData?.tabs]);

  const [selectedTabId, setSelectedTabId] = useState<number>(0);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_fee_section");
  //   }
  // }, []);

  // Set initial selected tab when activeTabs becomes available
  useEffect(() => {
    if (activeTabs.length > 0 && selectedTabId === 0) {
      setSelectedTabId(activeTabs[0].id);
      previousTabIdRef.current = activeTabs[0].id;
    }
  }, [activeTabs, selectedTabId]);

  // Track tab switching
  const handleTabChange = useCallback(
    (tabId: number) => {
      // Only track if tab actually changed (not initial render)
      if (
        previousTabIdRef.current !== tabId &&
        previousTabIdRef.current !== 0
      ) {
        const selectedTab = activeTabs.find((tab) => tab.id === tabId);
        tabSwitched({
          section: "program_fee_section",
          component: "fee_tabs",
          label: selectedTab?.name || `tab_${tabId}`,
        });
      }
      previousTabIdRef.current = tabId;
      setSelectedTabId(tabId);
    },
    [activeTabs],
  );

  // Handle CTA button click with tracking
  const handleOpenLeadForm = useCallback(() => {
    leadCtaClick({
      section: "program_fee_section",
      component: "cta_button",
      label: feeData?.cta_button_text || "",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [dispatch, feeData?.cta_button_text]);

  // Get rows for the selected tab
  const tabRows = useMemo(() => {
    if (!feeData?.rows) return [];
    return feeData.rows
      .filter(
        (row) =>
          row.fee_type_id === selectedTabId && row.is_active && row.status,
      )
      .sort((a, b) => a.order_index - b.order_index);
  }, [feeData?.rows, selectedTabId]);

  // Get columns for the selected tab
  const tabColumns = useMemo(() => {
    if (!feeData?.columns) return [];
    return feeData.columns
      .filter(
        (col) =>
          col.fee_type_id === selectedTabId && col.is_active && col.status,
      )
      .sort((a, b) => a.order_index - b.order_index);
  }, [feeData?.columns, selectedTabId]);

  // Get values for the selected tab
  const tabValues = useMemo(() => {
    if (!feeData?.values) return [];
    return feeData.values.filter(
      (val) => val.fee_type_id === selectedTabId && val.is_active && val.status,
    );
  }, [feeData?.values, selectedTabId]);

  // Get value for a specific row and column
  const getValue = (rowId: number, columnId: number): FeeValue | undefined => {
    return tabValues.find(
      (val) => val.category_id === rowId && val.semester_id === columnId,
    );
  };

  const selectedTab = activeTabs.find((tab) => tab.id === selectedTabId);

  // Don't render if no valid data
  if (!feeData?.status || activeTabs.length === 0) {
    return null;
  }

  const {
    title = "Semester Fee",
    title_text_color = "#000000",
    note = "",
    note_text_color = "#BE2330",
    background_color = "#FFFFFF",
    table_header_background_color = "#BE2330",
    cta_button_text = "",
    cta_button_text_color = "#FFFFFF",
    cta_button_color = "#BE2330",
    cta_button_icon = "",
    cta_button_icon_alt = "",
  } = feeData;

  return (
    <section
      ref={ref}
      className={cn(
        "flex items-center justify-center gap-6 w-full",
        "px-5 py-8 sm:px-8 sm:py-11",

        className,
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "flex flex-col items-start justify-start gap-4 w-full",
          "sm:w-[1008px] md:w-[1158px] lg:w-[1358px] xl:w-[1678px]",
        )}
      >
        <div className="flex flex-col gap-3 sm:flex-row justify-between items-center sm:justify-between sm:items-start w-full">
          {title && (
            <h2
              className={cn(
                "text-heading-6-semibold",
                "sm:text-heading-5-semibold",
                "md:text-heading-4-semibold",
                "lg:text-new-heading-34-semibold",
                "xl:text-new-heading-42-semibold",
                "line-clamp-1",
                "type-h3",
              )}
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
          )}
        </div>

        {activeTabs.length > 1 && (
          <div
            className={cn(
              "flex flex-wrap",
              // Mobile: pack the pills left with one consistent gap on BOTH axes.
              // `justify-between` here spread each line's pills to the container edges —
              // with 3 tabs that left a large hole between the two on line 1 while line 2
              // sat flush against it, since a wrapping row has no row-gap of its own.
              // A plain `gap` (not gap-x/gap-y) so `sm:gap-4` cleanly overrides it.
              "gap-3",
              "xs:w-full",
              "sm:justify-start sm:gap-4",
            )}
          >
            {activeTabs.map((tab) => {
              const isSelected = tab.id === selectedTabId;
              return (
                <button
                  key={tab.id}
                  className={cn(
                    "xs:px-6 xs:py-[6px]",
                    "sm:text-heading-8-regular",
                    "md:text-heading-7-semibold",
                    "lg:text-heading-7-semibold",
                    "xl:text-heading-6-semibold",
                    "rounded-lg px-4 py-2",
                    "px-6",
                    "type-h6",
                  )}
                  style={{
                    backgroundColor: isSelected
                      ? tab.active_bg_color || undefined
                      : tab.bg_color || undefined,
                    color: isSelected
                      ? tab.active_text_color || tab.text_color || undefined
                      : tab.text_color || undefined,
                  }}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Table */}
        <div className=" bg-white rounded-[12px] overflow-hidden border border-gray-200 w-full">
          <div className={cn(isMobile ? "overflow-x-auto smooth-scroll" : "")}>
            <Table
              className="overflow-hidden rounded-4 "
              style={isMobile ? { tableLayout: "fixed" } : undefined}
            >
              <TableHeader
                className="p-4 [&_tr]:border-0"
                style={{ backgroundColor: table_header_background_color }}
              >
                <TableRow
                  className="border-0 hover:bg-transparent"
                  style={{ backgroundColor: table_header_background_color }}
                >
                  {/* Category Header */}
                  <TableHead
                    className={cn(
                      "h-10",
                      "xs:pl-[18px] xs:py-2 xs:min-w-[400px] xs:w-[400px] xs:text-heading-9-semibold ",
                      "sm:w-[180px]",
                      "md:py-[18px] md:text-heading-8-semibold",
                      "text-white",

                      "xl:w-[200px] xl:p-[18px] xl:text-heading-6-semibold",
                      "border-0",
                      "type-h6",
                    )}
                    style={{
                      color:
                        selectedTab?.category_header_text_color || "#FFFFFF",
                      backgroundColor:
                        selectedTab?.category_header_bg_color || undefined,
                      ...(isMobile && {
                        minWidth: "200px",
                        width: "200px",
                      }),
                    }}
                  >
                    {selectedTab?.category_header}
                  </TableHead>

                  {/* Column Headers */}
                  {tabColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={cn(
                        "w-[80px] ",
                        "xs:text-heading-9-semibold",
                        "md:text-heading-8-semibold",
                        "md:w-[90px]",
                        "lg:w-[120px]",
                        "xl:w-[180px] xl:text-heading-6-semibold",
                        "text-white",
                        "border-0",
                        "type-h6",
                      )}
                      style={{
                        color: column.text_color || "#FFFFFF",
                        backgroundColor: column.bg_color || undefined,
                        // Mobile uses table-layout:fixed, which pins each column to its
                        // header width and ignores content. 80px is too narrow for labels
                        // like "Registration" / "Total (Lumpsum)", so they overflowed and
                        // overlapped the next column. Give value columns enough width; the
                        // table stays horizontally scrollable via the overflow-x-auto wrapper.
                        ...(isMobile && {
                          minWidth: "130px",
                          width: "130px",
                        }),
                      }}
                    >
                      {column.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {tabRows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="text-black text-opacity-80 text-start hover:bg-transparent"
                    style={{
                      backgroundColor: row.bg_color || undefined,
                    }}
                  >
                    {/* Row Name */}
                    <TableCell
                      className={cn(
                        "w-[180px] align-middle",
                        "xl:w-[200px]",
                        "sm:text-heading-9-regular",
                        "md:text-heading-8-regular",
                        "xl:text-new-body-24-regular xl:p-[18px] ",
                        "xs:text-heading-8-medium xs:pl-[18px] xs:py-3 xs:min-w-[400px] xs:w-[400px] ",
                        "type-body-sm",
                      )}
                      style={{
                        color: row.text_color || undefined,
                        borderColor: row.border_color || undefined,
                        ...(isMobile && {
                          minWidth: "400px",
                          width: "400px",
                        }),
                      }}
                    >
                      {row.name}
                    </TableCell>

                    {/* Row Values */}
                    {tabColumns.map((column) => {
                      const cellValue = getValue(row.id, column.id);
                      return (
                        <TableCell
                          key={column.id}
                          className={cn(
                            "align-middle",
                            "w-[80px]",
                            "sm:text-heading-9-regular",
                            "md:w-[90px] md:text-heading-8-regular",
                            "lg:w-[120px]",
                            "xl:w-[180px] xl:text-new-body-24-regular",
                            "xs:text-heading-8-regular",
                            "type-body-sm",
                          )}
                          style={{
                            color: cellValue?.text_color || undefined,
                            backgroundColor: cellValue?.bg_color || undefined,
                            borderColor: cellValue?.border_color || undefined,
                          }}
                        >
                          {cellValue?.value || "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {note && (
          <div>
            <p
              className={cn(
                "text-body-3-regular",
                "xs:text-heading-8-medium",
                "sm:text-heading-9-semibold",
                "md:text-heading-8-semibold",
                "lg:text-heading-8-semibold",
                "xl:text-new-body-24-regular",
                "type=body-sm",
              )}
              style={{ color: note_text_color }}
            >
              {note}
            </p>
          </div>
        )}
        <div className="flex justify-center items-center w-full">
          {cta_button_text && (
            <Button
              className={cn(
                "text-heading-9-semibold flex gap-[6px] ",
                "sm:text-heading-9-semibold sm:gap-[6px] sm:px-[14px] sm:py-[6px]",
                "md:text-heading-7-semibold md:gap-[6px] md:px-[16px] md:py-[6px]",
                "lg:text-heading-6-semibold lg:gap-[10px] lg:py-2 lg:px-6 ",
                "xl:text-heading-4-semibold xl:gap-[10px] xl:py-2 xl:px-6 ",
              )}
              style={{
                backgroundColor: cta_button_color,
                color: cta_button_text_color,
              }}
              onClick={handleOpenLeadForm}
            >
              {cta_button_text}
              {cta_button_icon && (
                <div
                  className={cn(
                    "relative",
                    "size-[18px]",
                    "sm:size-[26px]",
                    "md:size-8",
                    "lg:size-9",
                    "xl:size-9",
                  )}
                >
                  <Image src={cta_button_icon} alt={cta_button_icon_alt} fill />
                </div>
              )}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

export default ProgramFee1;
